import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, Calendar, Mail, Phone, ArrowRight, Home } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { businessInfo } from '../data/mock';

export const BookingConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const bookingDetails = location.state?.bookingDetails;

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Appointment Request Confirmed | Precision Mobile Detailing';
    
    // If no booking details, redirect to booking page
    if (!bookingDetails) {
      navigate('/booking');
    }
  }, [bookingDetails, navigate]);

  // If somehow accessed without booking details
  if (!bookingDetails) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white py-20">
      <div className="container mx-auto px-6">
        <div className="max-w-3xl mx-auto">
          {/* Success Icon & Header */}
          <div className="text-center mb-12">
            <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <CheckCircle className="w-16 h-16 text-green-600" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Appointment Request Confirmed!
            </h1>
            <p className="text-xl text-gray-600">
              Thank you for choosing Precision Mobile Detailing
            </p>
          </div>

          {/* Confirmation Details Card */}
          <Card className="mb-8 border-2 border-gray-200 shadow-xl">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <Calendar className="w-6 h-6" />
                Your Appointment Details
              </h2>
              
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Name</p>
                    <p className="font-semibold text-lg">{bookingDetails.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Phone</p>
                    <p className="font-semibold text-lg">{bookingDetails.phone}</p>
                  </div>
                </div>

                {bookingDetails.email && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Email</p>
                    <p className="font-semibold text-lg">{bookingDetails.email}</p>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Service</p>
                    <p className="font-semibold text-lg">{bookingDetails.service}</p>
                  </div>
                  {bookingDetails.vehicleType && (
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Vehicle Type</p>
                      <p className="font-semibold text-lg">{bookingDetails.vehicleType}</p>
                    </div>
                  )}
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-1">Location</p>
                  <p className="font-semibold text-lg">{bookingDetails.location}</p>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Preferred Date</p>
                    <p className="font-semibold text-lg">{new Date(bookingDetails.preferredDate).toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}</p>
                  </div>
                  {bookingDetails.preferredTime && (
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Preferred Time</p>
                      <p className="font-semibold text-lg">{bookingDetails.preferredTime}</p>
                    </div>
                  )}
                </div>

                {bookingDetails.notes && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Additional Notes</p>
                    <p className="font-semibold">{bookingDetails.notes}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* What Happens Next */}
          <Card className="mb-8 bg-blue-50 border-2 border-blue-200">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold mb-6">What Happens Next?</h2>
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                    1
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">Email Confirmation</h3>
                    <p className="text-gray-700">
                      You'll receive an email confirmation at {bookingDetails.email || 'your provided email'} within a few minutes.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                    2
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">We'll Contact You</h3>
                    <p className="text-gray-700">
                      Our team will call you at {bookingDetails.phone} within 24 hours to confirm your appointment and answer any questions.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                    3
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">We Come to You</h3>
                    <p className="text-gray-700">
                      On your scheduled date, we'll arrive at {bookingDetails.location} with all equipment and supplies ready to detail your vehicle.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Info */}
          <Card className="mb-8">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold mb-4">Need to Make Changes?</h2>
              <p className="text-gray-600 mb-6">
                If you need to modify or cancel your appointment, please contact us:
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href={`tel:${businessInfo.phone}`}
                  className="flex items-center gap-2 text-lg font-semibold hover:text-gray-700"
                >
                  <Phone className="w-5 h-5" />
                  {businessInfo.phone}
                </a>
                <a 
                  href={`mailto:${businessInfo.email}`}
                  className="flex items-center gap-2 text-lg font-semibold hover:text-gray-700"
                >
                  <Mail className="w-5 h-5" />
                  {businessInfo.email}
                </a>
              </div>
            </CardContent>
          </Card>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="cta-button text-lg px-8 py-6">
              <Link to="/">
                <Home className="mr-2 w-5 h-5" />
                Back to Home
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="text-lg px-8 py-6">
              <Link to="/services">
                View Our Services
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </div>

          {/* Social Proof */}
          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-4">
              Join hundreds of satisfied customers across the Inland Empire
            </p>
            <div className="flex items-center justify-center gap-2 text-yellow-500">
              {[...Array(5)].map((_, i) => (
                <svg key={i} className="w-6 h-6 fill-current" viewBox="0 0 20 20">
                  <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                </svg>
              ))}
            </div>
            <p className="text-gray-700 font-semibold mt-2">5.0 Rating • 30+ Reviews</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmation;
